package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.dsl.ir.model.animation.UiAnimation
import top.wcpe.allincore.ui.runtime.animation.AnimationHandle
import top.wcpe.allincore.ui.runtime.animation.AnimationScheduler
import top.wcpe.allincore.ui.runtime.render.model.GradientStop
import top.wcpe.allincore.ui.runtime.style.CornerRadii
import kotlin.jvm.JvmStatic

/**
 * 进度条。
 *
 * v2 起走 styled 渲染路径：背景与前景都支持 [top.wcpe.allincore.ui.runtime.style.StyleVisual]
 * 的圆角 / 边框 / 渐变 / 阴影 / effects。前景额外支持独立的 `bar*` 渐变与圆角字段，
 * 默认为 null 时回落到 [barColor]（纯色）+ 共享背景圆角。
 */
@Suppress("TooManyFunctions", "MagicNumber")
class ProgressBar private constructor() : UIElement<ProgressBar>() {

    private var valueField: Double = 0.0
    private var maxField: Double = 1.0

    // ========== 进度过渡 ==========
    /** 渲染实际读取的显示值；开启过渡时由动画调度器逐帧写入，向 [valueField] 收敛。 */
    private var displayValueField: Double = 0.0

    /** 是否已收到过首次 value 赋值：首次直接落位，避免每次开界面都播一段 0 → value 的增长动画。 */
    private var displayInitialized: Boolean = false

    /** 是否对 value 变化启用过渡，默认开启。 */
    private var animateValueField: Boolean = true

    /** 过渡时长（毫秒）。 */
    private var valueTransitionMsField: Double = DEFAULT_TRANSITION_MS

    /** 过渡模板缓存：时长未变时复用，避免每次值变化都新建 IR 对象。 */
    private var transitionTemplateField: UiAnimation? = null

    /** 当前正在跑的过渡句柄；关闭过渡时用于立即终止。 */
    private var transitionHandleField: AnimationHandle? = null

    /** 前景纯色（ARGB 整数）；0 表示未指定，渲染时回落到主题 primary。 */
    private var barColorField: Int = 0

    /**
     * 等分刻度数；0 或负值表示不绘制刻度，>1 时把进度条沿 X 轴等分为 `notches` 段，
     * 在内部分段位置绘制 1px 半透黑色竖线。常用于 vanilla boss 血条 NOTCHED_6 / 10 / 12 / 20 复刻。
     */
    private var notchesField: Int = 0

    /** Task 2a：等分刻度线颜色覆盖（ARGB）；null 时回退主题 progressBarNotchColor。 */
    private var notchColorField: Int? = null

    // ========== 前景独立圆角 ==========
    /** 前景独立圆角；null 时随宿主 visual.cornerRadii。 */
    private var barCornerRadiiField: CornerRadii? = null

    // ========== 前景渐变 ==========
    /** `none / linear / radial`；null 走纯色 barColor。 */
    private var barGradientTypeField: String? = null

    /** CSS 角度（0°=下→上，90°=左→右，180°=上→下，270°=右→左）。 */
    private var barGradientAngleField: Float? = null
    private var barGradientStartColorField: Int? = null
    private var barGradientEndColorField: Int? = null

    /** 多停靠点；非空时优先于 start/end。 */
    private var barGradientStopsField: List<GradientStop>? = null
    private var barRadialCenterXField: Float? = null
    private var barRadialCenterYField: Float? = null
    private var barRadialRadiusField: Float? = null

    override fun self(): ProgressBar = this

    fun value(v: Double): ProgressBar = apply {
        valueField = v
        startValueTransition(v)
    }
    fun max(v: Double): ProgressBar = apply { maxField = v }
    fun barColor(argb: Int): ProgressBar = apply { barColorField = argb }
    fun notches(n: Int): ProgressBar = apply { notchesField = n }

    fun value(): Double = valueField
    fun max(): Double = maxField
    fun barColor(): Int = barColorField
    fun notches(): Int = notchesField

    // ========== 进度过渡 ==========

    /**
     * 渲染读取的显示值：开启过渡时是逐帧收敛中的中间值，关闭过渡时恒等于 [value]。
     */
    fun displayValue(): Double = displayValueField

    /**
     * 动画系统写入当前显示值，不改目标值 [value]，也不递归触发新过渡。
     *
     * 与 [Slider.setAnimatedValue] 同构，由 AnimationEngine 的静默通道调用。
     */
    fun setAnimatedValue(v: Double): ProgressBar = apply { displayValueField = v }

    /** 是否对 value 变化启用过渡（默认 true）；关闭时立即落位到当前目标值。 */
    fun animateValue(v: Boolean): ProgressBar = apply {
        animateValueField = v
        if (!v) {
            transitionHandleField?.cancel()
            transitionHandleField = null
            displayValueField = valueField
        }
    }

    fun animateValue(): Boolean = animateValueField

    /** 过渡时长（毫秒）；非正值等同于关闭过渡。 */
    fun valueTransitionMs(v: Double): ProgressBar = apply { valueTransitionMsField = v }
    fun valueTransitionMs(): Double = valueTransitionMsField

    /**
     * 把显示值向新目标值收敛。
     *
     * 性能：只有「已初始化 + 开启过渡 + 值真的变了 + 已挂上调度器」才创建一个过渡动画，
     * 跑完由调度器自动移除；静止态既不注册每帧回调也不标渲染脏。关闭过渡时整条路径退化为一次赋值。
     */
    private fun startValueTransition(target: Double) {
        val scheduler = animationScheduler() as? AnimationScheduler
        if (!displayInitialized || !animateValueField || valueTransitionMsField <= 0.0 ||
            scheduler == null || displayValueField == target
        ) {
            displayInitialized = true
            displayValueField = target
            return
        }
        // 同一元素同一属性的旧过渡由调度器在注册新过渡时取消，这里无需手动清理。
        transitionHandleField = scheduler.transition(this, "value", displayValueField, target, transitionTemplate())
    }

    /** 过渡模板：时长未变时复用同一个 IR 对象。 */
    private fun transitionTemplate(): UiAnimation {
        val cached = transitionTemplateField
        if (cached != null && cached.durationMs == valueTransitionMsField) {
            return cached
        }
        val created = UiAnimation(
            UiAnimation.Kind.TRANSITION,
            valueTransitionMsField,
            0.0,
            0.0,
            TRANSITION_EASING,
            emptyMap(),
            emptyList(),
            emptyList(),
        )
        transitionTemplateField = created
        return created
    }

    /** Task 2a：刻度线颜色覆盖；null 回退主题 progressBarNotchColor。 */
    fun notchColor(v: Int?): ProgressBar = apply { notchColorField = v }
    fun notchColor(): Int? = notchColorField

    // ========== 前景圆角 ==========

    fun barCornerRadii(v: CornerRadii?): ProgressBar = apply { barCornerRadiiField = v }
    fun barCornerRadii(): CornerRadii? = barCornerRadiiField

    /** 兼容入口：等值四角；null 视作清除。 */
    fun barCornerRadius(v: Float?): ProgressBar = apply {
        barCornerRadiiField = v?.let { CornerRadii.uniform(it) }
    }

    /** 兼容 getter：取 TL；null 时返回 null。 */
    fun barCornerRadius(): Float? = barCornerRadiiField?.tl

    // ========== 前景渐变 ==========

    fun barGradientType(v: String?): ProgressBar = apply { barGradientTypeField = v }
    fun barGradientType(): String? = barGradientTypeField

    fun barGradientAngle(v: Float?): ProgressBar = apply { barGradientAngleField = v }
    fun barGradientAngle(): Float? = barGradientAngleField

    fun barGradientStartColor(v: Int?): ProgressBar = apply { barGradientStartColorField = v }
    fun barGradientStartColor(): Int? = barGradientStartColorField

    fun barGradientEndColor(v: Int?): ProgressBar = apply { barGradientEndColorField = v }
    fun barGradientEndColor(): Int? = barGradientEndColorField

    fun barGradientStops(v: List<GradientStop>?): ProgressBar = apply {
        barGradientStopsField = v?.let { it.toList() }
    }
    fun barGradientStops(): List<GradientStop>? = barGradientStopsField

    fun barRadialCenterX(v: Float?): ProgressBar = apply { barRadialCenterXField = v }
    fun barRadialCenterX(): Float? = barRadialCenterXField

    fun barRadialCenterY(v: Float?): ProgressBar = apply { barRadialCenterYField = v }
    fun barRadialCenterY(): Float? = barRadialCenterYField

    fun barRadialRadius(v: Float?): ProgressBar = apply { barRadialRadiusField = v }
    fun barRadialRadius(): Float? = barRadialRadiusField

    /** 是否声明了前景渐变（type 非空 + 至少一种颜色源）。 */
    fun hasBarGradient(): Boolean {
        val type = barGradientTypeField
        if (type.isNullOrBlank() || "none".equals(type.trim(), ignoreCase = true)) {
            return false
        }
        if (!barGradientStopsField.isNullOrEmpty()) {
            return true
        }
        return barGradientStartColorField != null && barGradientEndColorField != null
    }

    companion object {
        /** 默认过渡时长（毫秒）。 */
        const val DEFAULT_TRANSITION_MS: Double = 200.0

        /** 过渡缓动：起步快、收尾缓，数值变化能被立刻察觉。 */
        const val TRANSITION_EASING: String = "easeOut"

        @JvmStatic
        fun create(): ProgressBar = ProgressBar()
    }
}
